import axiosInstance from '@/lib/axios/config/axiosConfig';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { CallQueueType } from '@/constants/queue';
import { fetchAgentsStatus, AgentStatus } from '@/lib/axios/api/widgets.api';
import { API_ENDPOINTS } from '@/lib/constants/apiEndpoints';
import { sortByLabel, sortByName } from '@/lib/sortByName';
import { getHttpErrorKind } from '@/utils/httpErrors';
import { redirectToUnauthorize } from '@/utils/authRedirect';

// User role enum
// eslint-disable-next-line react-refresh/only-export-components
export enum UserRole {
  ADMIN = 'Admin',
  MANAGER = 'Manager'
}

// Data interfaces
export interface Queue {
  label: string;
  microsoftId: string;
  type: string;
  agentMicrosoftIds: string[];
}

export interface Agent {
  name: string;
  microsoftId: string;
  queueId?: string,
  queueType: CallQueueType
}

export interface QueueResponse {
  id: number;
  microsoftId: string;
  name: string;
  agents: Agent[];
  type: string;
}

export interface QueueWithRoleResponse {
  userRole: UserRole;
  queues: QueueResponse[];
}

export interface AgentStatusItem {
  name: string;
  microsoftId: string;
}

function removeAgentFromQueue(queue: QueueResponse, microsoftUserId: string): QueueResponse {
  return { ...queue, agents: queue.agents.filter(agent => agent.microsoftId !== microsoftUserId) };
}

// Context interface
interface AppDataContextType {
  // Queue data
  queues: Queue[];
  directAgentGroups: Queue[];
  agents: Agent[];
  queuesResponse: QueueResponse[];
  directAgentGroupIds: number[];
  queueMicrosoftIds: string[];
  uniqueAgents: Agent[];
  userRole: UserRole | null;
  // Bumped when a with-agents-and-role poll detects real data change — active tabs use this to reload
  queuesDataVersion: number;

  // Realtime agent status data (availability/activity), sourced from /agents/status
  agentsStatus: AgentStatusItem[];
  // Same poll, full raw shape (queueIds/directLineNumber/etc.) for consumers that need more than name+id.
  agentsStatusRaw: AgentStatus[];
  agentsStatusLoading: boolean;
  agentsStatusError: string | null;
  refetchAgentsStatus: () => void;

  // Loading states
  loaded: boolean;

  // Error states
  errors: {
    queues: string | null;
    agents: string | null;
  };

  // Refresh methods
  fetchQueuesWithRole: () => Promise<void>;
  removeAgentByMicrosoftId: (microsoftUserId: string) => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [queuesResponse, setQueuesResponse] = useState<QueueResponse[]>([]);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const { gromToken, microsoftToken, getGromToken } = useAuth();

  // Queue and Agent states - removed individual states, will use useMemo instead

  const directAgentGroupIds = useMemo(() => {
    return queuesResponse.filter(queue => queue.type === CallQueueType.DIRECT_AGENT_GROUP).map(queue => queue.id) || [];
  }, [queuesResponse]);

  const queueMicrosoftIds = useMemo(() => {
    return queuesResponse.filter(queue => queue.type === CallQueueType.CALL_QUEUE).map(queue => queue.microsoftId) || [];
  }, [queuesResponse]);

  const getQueueId = (queue: QueueResponse) => {
    return queue.type === CallQueueType.CALL_QUEUE ? queue.microsoftId : queue.id?.toString();
  }

  // Computed values from queuesResponse - no setState needed, instant access
  const queues = useMemo(() => {
    const mappedQueues = queuesResponse.map((queue) => ({
      label: queue.name,
      microsoftId: getQueueId(queue),
      type: queue.type,
      agentMicrosoftIds: queue.agents.map(agent => agent.microsoftId)
    }));
    return sortByLabel(mappedQueues.filter(queue => queue.type === CallQueueType.CALL_QUEUE));
  }, [queuesResponse]);

  const directAgentGroups = useMemo(() => {
    const mappedQueues = queuesResponse.map((queue) => ({
      label: queue.name,
      microsoftId: getQueueId(queue),
      type: queue.type,
      agentMicrosoftIds: queue.agents.map(agent => agent.microsoftId)
    }));
    return sortByLabel(mappedQueues.filter(queue => queue.type === CallQueueType.DIRECT_AGENT_GROUP));
  }, [queuesResponse]);

  const agents = useMemo(() => {
    return sortByName(queuesResponse.flatMap((queue) =>
      queue.agents?.map((agent) => ({
        name: agent.name,
        microsoftId: agent.microsoftId,
        queueType: queue.type as CallQueueType,
        queueId: getQueueId(queue)
      })) || []
    ));
  }, [queuesResponse]);

  const uniqueAgents = useMemo(() => {
    return Array.from(
      new Map(agents.map((agent) => [agent.microsoftId, agent])).values()
    );
  }, [agents]);


  // Bumped whenever a with-agents-and-role poll returns queues that differ from what we have —
  // active tabs listen for this to force a full data reload (TCX interval-diff requirement).
  // Unmounted (inactive) tabs simply don't run their effects, so only the active tab reacts.
  const [queuesDataVersion, setQueuesDataVersion] = useState(0);
  const prevQueuesJsonRef = useRef<string | null>(null);

  // Loading states
  const [loaded, setLoaded] = useState(false);

  // Error states
  const [errors, setErrors] = useState({
    queues: null as string | null,
    agents: null as string | null
  });

  // Fetch queues with agents and user role
  const hasLoadedQueuesRef = useRef(false);
  // TCX-6500: a 401/403 from this endpoint triggers an immediate Grom token re-fetch (no page
  // reload) and keeps retrying on the regular 5s poll cadence; only after 3 consecutive failures
  // does it give up and send the user to the unauthorize page. The interceptor skips its usual
  // immediate-redirect behavior for this endpoint specifically so this can run.
  const roleUnauthorizedAttemptsRef = useRef(0);
  const ROLE_UNAUTHORIZED_MAX_ATTEMPTS = 3;
  const isFetchingFreshTokenRef = useRef(false);

  const fetchQueuesWithRole = useCallback(async () => {
    try {
      // Only show the loading state for the initial fetch — background polls refresh silently
      // and must not flip `loaded` (a memo dep downstream) unless the data actually changed,
      // or every 5s poll would cascade into unrelated widget refetches (TCX-6338 regression).
      if (!hasLoadedQueuesRef.current) setLoaded(false);
      const response = await axiosInstance.get<QueueWithRoleResponse>(API_ENDPOINTS.QUEUES.WITH_AGENTS_AND_ROLE);
      const nextQueues = response.data.queues || [];
      const nextQueuesJson = JSON.stringify(nextQueues);
      const isFirstLoad = prevQueuesJsonRef.current === null;
      const changed = prevQueuesJsonRef.current !== nextQueuesJson;

      if (changed) {
        prevQueuesJsonRef.current = nextQueuesJson;
        setQueuesResponse(nextQueues);
        if (!isFirstLoad) setQueuesDataVersion(v => v + 1);
      }
      setUserRole(response.data.userRole);
      setErrors(prev => ({ ...prev, queues: null }));
      roleUnauthorizedAttemptsRef.current = 0;
    } catch (err) {
      const errorKind = getHttpErrorKind(err);
      if (errorKind === 'auth' || errorKind === 'forbidden') {
        roleUnauthorizedAttemptsRef.current += 1;
        if (roleUnauthorizedAttemptsRef.current >= ROLE_UNAUTHORIZED_MAX_ATTEMPTS) {
          roleUnauthorizedAttemptsRef.current = 0;
          redirectToUnauthorize();
        } else if (microsoftToken && !isFetchingFreshTokenRef.current) {
          // A 401 here means the Grom token is bad right now — go fetch a fresh one directly
          // instead of dispatching 'token:refresh-required', which only refreshes when the
          // token's own exp claim says it's *about* to expire and would otherwise no-op here.
          isFetchingFreshTokenRef.current = true;
          getGromToken(microsoftToken)
            .catch(() => {
              // getGromToken already redirects on auth/forbidden itself; any other error just
              // means the next 5s poll retries as before.
            })
            .finally(() => {
              isFetchingFreshTokenRef.current = false;
            });
        }
      }
      setErrors(prev => ({
        ...prev,
        queues: 'Failed to fetch queue data with role'
      }));
      console.error('Error fetching queues with role:', err);
    } finally {
      setLoaded(true);
      hasLoadedQueuesRef.current = true;
    }
  }, [microsoftToken, getGromToken]);

  // Remove agent from all queues by Microsoft User ID
  const removeAgentByMicrosoftId = useCallback((microsoftUserId: string) => {
    console.log('AppDataContext: Removing agent with microsoftUserId:', microsoftUserId);
    setQueuesResponse(prevQueues => prevQueues.map(queue => removeAgentFromQueue(queue, microsoftUserId)));
  }, []);

  // Initial data fetch + poll every 5s so filter dropdowns stay current
  useEffect(() => {
    if (gromToken?.length > 0) {
      fetchQueuesWithRole();
      const intervalId = setInterval(fetchQueuesWithRole, 5000);
      return () => clearInterval(intervalId);
    }
  }, [gromToken, fetchQueuesWithRole]);

  // Realtime agent status (availability/activity) - separate endpoint from queues/agents above
  const [agentsStatus, setAgentsStatus] = useState<AgentStatusItem[]>([]);
  const [agentsStatusRaw, setAgentsStatusRaw] = useState<AgentStatus[]>([]);
  const [agentsStatusLoading, setAgentsStatusLoading] = useState(true);
  const [agentsStatusError, setAgentsStatusError] = useState<string | null>(null);
  const agentsStatusControllerRef = useRef<AbortController | null>(null);

  const hasLoadedAgentsStatusRef = useRef(false);
  const refetchAgentsStatus = useCallback(() => {
    agentsStatusControllerRef.current?.abort();
    const controller = new AbortController();
    agentsStatusControllerRef.current = controller;
    // Same rationale as fetchQueuesWithRole above: don't toggle the loading flag or the
    // array reference on background polls unless the data actually changed.
    if (!hasLoadedAgentsStatusRef.current) setAgentsStatusLoading(true);
    fetchAgentsStatus({ signal: controller.signal })
      .then(data => {
        const nextAgentsStatus = data.map(a => ({ name: a.agentName, microsoftId: a.agentId }));
        setAgentsStatus(prev => (JSON.stringify(prev) === JSON.stringify(nextAgentsStatus) ? prev : nextAgentsStatus));
        setAgentsStatusRaw(prev => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
        setAgentsStatusError(null);
        setAgentsStatusLoading(false);
        hasLoadedAgentsStatusRef.current = true;
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setAgentsStatusError('Failed to fetch agents');
        setAgentsStatusLoading(false);
        hasLoadedAgentsStatusRef.current = true;
      });
  }, []);

  useEffect(() => {
    if (gromToken?.length > 0) {
      refetchAgentsStatus();
      const intervalId = setInterval(refetchAgentsStatus, 5000);
      return () => {
        clearInterval(intervalId);
        agentsStatusControllerRef.current?.abort();
      };
    }
    return () => agentsStatusControllerRef.current?.abort();
  }, [gromToken, refetchAgentsStatus]);

  const contextValue = useMemo(
    () => ({
      queuesResponse,
      uniqueAgents,
      queues,
      directAgentGroups,
      directAgentGroupIds,
      queueMicrosoftIds,
      agents,
      userRole,
      queuesDataVersion,
      agentsStatus,
      agentsStatusRaw,
      agentsStatusLoading,
      agentsStatusError,
      refetchAgentsStatus,
      loaded,
      errors,
      fetchQueuesWithRole,
      removeAgentByMicrosoftId,
    }),
    [
      queuesResponse,
      uniqueAgents,
      queues,
      directAgentGroups,
      directAgentGroupIds,
      queueMicrosoftIds,
      agents,
      userRole,
      queuesDataVersion,
      agentsStatus,
      agentsStatusRaw,
      agentsStatusLoading,
      agentsStatusError,
      refetchAgentsStatus,
      loaded,
      errors,
      fetchQueuesWithRole,
      removeAgentByMicrosoftId,
    ]
  );

  return (
    <AppDataContext.Provider value={contextValue}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
}

export const WelcomeMessage = "Welcome to TCX Insight Reporting";
